import { useEffect, useRef, useState } from 'react';
import auraLogo from '@/assets/uma-aura-logo.svg';
import logoAnimation from '@/assets/diwali-header-logo.mp4';
import logoPoster from '@/assets/diwali-header-logo-poster.webp';

type Connection = EventTarget & { saveData?: boolean };
const connection = () => (navigator as Navigator & { connection?: Connection }).connection;
const motionAllowed = () => typeof window !== 'undefined'
  && !window.matchMedia('(prefers-reduced-motion: reduce)').matches && !connection()?.saveData;

/** The parent remains the same accessible home link. This media is decorative. */
export function AnimatedHeaderLogo() {
  const [animate, setAnimate] = useState(false);
  const [failed, setFailed] = useState(false);
  const [playing, setPlaying] = useState(false);
  const videoRef = useRef<HTMLVideoElement>(null);
  const showVideo = animate && !failed;

  useEffect(() => {
    const preference = window.matchMedia('(prefers-reduced-motion: reduce)');
    const network = connection();
    const update = () => setAnimate(motionAllowed());
    update();
    preference.addEventListener('change', update);
    network?.addEventListener('change', update);
    return () => { preference.removeEventListener('change', update); network?.removeEventListener('change', update); };
  }, []);

  useEffect(() => {
    const video = videoRef.current;
    if (!showVideo || !video) { setPlaying(false); return; }
    let disposed = false;
    video.muted = video.defaultMuted = true;
    const resume = () => {
      if (document.hidden) { video.pause(); return; }
      void video.play().catch(() => { if (!disposed) { setFailed(true); setPlaying(false); } });
    };
    document.addEventListener('visibilitychange', resume);
    resume();
    return () => {
      disposed = true;
      document.removeEventListener('visibilitychange', resume);
      video.pause();
      video.removeAttribute('src');
      video.load();
    };
  }, [showVideo]);

  return <span className="animated-header-logo relative block h-16 w-24 shrink-0 overflow-hidden sm:h-[72px] sm:w-36 lg:w-40" aria-hidden="true">
    <img src={showVideo ? logoPoster : auraLogo} alt="" width={480} height={426}
      className="absolute inset-0 h-full w-full object-contain" fetchPriority="high" />
    {showVideo && <video ref={videoRef} src={logoAnimation} poster={logoPoster} autoPlay muted loop playsInline
      preload="auto" disablePictureInPicture tabIndex={-1}
      className={`pointer-events-none absolute inset-0 h-full w-full object-contain transition-opacity duration-200 ${playing ? 'opacity-100' : 'opacity-0'}`}
      onPlaying={() => setPlaying(true)} onError={() => { setFailed(true); setPlaying(false); }} />}
  </span>;
}
